import React, { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { PrimaryActionButton } from "@/components/ui/PrimaryActionButton";
import { GlassPanel } from "@/components/ui/GlassPanel";
import { Link2Off } from "lucide-react";
import { useTranslation } from "react-i18next";

interface SocialAccountCardProps {
  title: string;
  username?: string;
  subtitle?: string;
  profilePictureUrl?: string;
  icon: ReactNode;
  isLoading?: boolean;
  variant?: 'default' | 'warning' | 'info';
  onDisconnect?: () => void;
  children?: ReactNode;
  actions?: ReactNode;
  statusDot?: ReactNode;
  badge?: string;
}

export function SocialAccountCard({
  title,
  username,
  subtitle,
  profilePictureUrl,
  icon,
  isLoading = false,
  variant = 'default',
  onDisconnect,
  children,
  actions,
  statusDot,
  badge
}: SocialAccountCardProps) {
  const { t } = useTranslation();
  const getVariantStyles = () => {
    switch (variant) {
      case 'warning':
        return 'bg-orange-50';
      case 'info':
        return 'bg-neutral-100 dark:bg-neutral-800';
      default:
        return 'bg-neutral-900';
    }
  };

  return (
    <GlassPanel className={`p-4 sm:p-6 ${variant === 'default' ? '' : ''}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-8">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
            {profilePictureUrl ? (
              <img
                src={profilePictureUrl}
                alt={title}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="text-white text-xl">
                {icon}
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className=" text-white">{title}</h3>
              {badge && (
                <span className="px-2 py-0.5 text-xs font-medium bg-blue-500/20 text-blue-400 rounded-full">
                  {badge}
                </span>
              )}
            </div>
            {username && (
              <p className="text-sm text-neutral-300">@{username}</p>
            )}
            {subtitle && (
              <p className="text-xs text-neutral-400">{subtitle}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {statusDot}
          {onDisconnect && (
            <Button
              onClick={onDisconnect}
              disabled={isLoading}
              size="sm"
              variant="dark"
              className="px-2 sm:px-3"
              aria-label={t("account_social.disconnect_aria")}
            >
              <span className="sm:hidden flex items-center">
                <Link2Off className="h-4 w-4" />
              </span>
              <span className="hidden sm:inline">{t("account_social.disconnect")}</span>
            </Button>
          )}
        </div>
      </div>

      {children && (
        <div className="space-y-3">
          {children}
        </div>
      )}

      {actions && (
        <div className="space-y-3 mt-4">
          {actions}
        </div>
      )}

      {/* Disconnect action moved to header */}
    </GlassPanel>
  );
}
 